import gsap from 'gsap';
import { Container, Graphics, Text } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';
import { formatCurrencyCelebrationAmount } from '../lib/formatCurrency.js';

/**
 * Bottom-center popup: shows backend win amount as-is (with decimals).
 * Amount is set from {@link setWinAmount} while paylines cycle (same total each step).
 */

export class WinFloater {
  /**
   * @param {import('../animation/AnimationController.js').AnimationController} animationController
   * @param {{ width: number, barTopY: number }} layout
   */
  constructor(animationController, layout) {
    this._ac = animationController;
    this._layout = layout;
    this.theme = gameConfig.theme;

    this._layoutFullW = layout.width;
    this._barTopY = layout.barTopY;
    this._currencyCode = 'FUN';
    this._currencyPrecision = 2;
    /** @type {string | undefined} */
    this._currencySymbol = undefined;
    this._pillH = 64;
    this._pillR = 20;
    this._pillW = Math.min(280, layout.width - 40);

    this.root = new Container();
    this.root.visible = false;
    this.root.eventMode = 'none';
    this.root.x = layout.width / 2;
    this.root.y = layout.barTopY - 8;

    this._bg = new Graphics();
    this._bg.eventMode = 'none';
    this.root.addChild(this._bg);
    this._paintPillBg();

    this._amountText = new Text({
      text: '',
      style: {
        fontFamily: this.theme.fontUi,
        fontSize: 32,
        fontWeight: '800',
        fill: this.theme.winGlow,
        letterSpacing: 0.5,
        dropShadow: {
          alpha: 0.85,
          angle: Math.PI / 2,
          blur: 6,
          color: this.theme.skyGlow,
          distance: 0,
        },
      },
    });
    this._amountText.anchor.set(0.5, 0.5);
    this._amountText.y = -this._pillH / 2;
    this.root.addChild(this._amountText);
  }

  _paintPillBg() {
    const r = this._pillR;
    const pillH = this._pillH;
    this._bg.clear();
    this._bg
      .roundRect(-this._pillW / 2, -pillH, this._pillW, pillH, r)
      .fill({ color: this.theme.panelDeep, alpha: 0.92 });
    this._bg.roundRect(-this._pillW / 2, -pillH, this._pillW, pillH, r).stroke({
      width: 2,
      color: this.theme.panelBorder,
      alpha: 0.65,
    });
  }

  /** Keep popup centered when bottom bar stretches full width. */
  setLayoutWidth(fullWidth) {
    this._layoutFullW = fullWidth;
    this._pillW = Math.min(280, fullWidth - 40);
    this.root.x = fullWidth / 2;
    this.root.y = this._barTopY - 8;
    this._paintPillBg();
  }

  /**
   * @param {{ currencyCode?: string, currencyPrecision?: number, currencySymbol?: string }} [opts]
   */
  setCurrency(opts = {}) {
    if (opts.currencyCode != null && String(opts.currencyCode).trim() !== '') {
      this._currencyCode = String(opts.currencyCode).trim().toUpperCase();
    }
    if (Number.isFinite(Number(opts.currencyPrecision))) {
      this._currencyPrecision = Number(opts.currencyPrecision);
    }
    const sym = opts.currencySymbol;
    this._currencySymbol =
      sym == null || String(sym).trim() === '' ? undefined : String(sym).trim();
  }

  _fmt(n) {
    return formatCurrencyCelebrationAmount(
      n,
      this._currencyCode,
      this._currencyPrecision,
      this._currencySymbol,
    );
  }

  reset() {
    gsap.killTweensOf(this.root);
    this.root.alpha = 1;
    this.root.scale.set(1);
    this.root.visible = false;
    this._amountText.text = '';
    this._amountText.scale.set(1);
  }

  /**
   * @param {number} amount full win for the spin (not per-line / not a running sum)
   */
  setWinAmount(amount) {
    this._amountText.text = this._fmt(amount);
    if (!this.root.visible) {
      this.root.visible = true;
      this.root.alpha = 0;
      this.root.scale.set(0.92);
      this._ac.runGsap(this.root, {
        alpha: 1,
        scale: 1,
        duration: 0.18,
        ease: 'back.out(2)',
      });
    }
  }
}
